import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { Box, CssBaseline, CircularProgress } from '@mui/material';
import { AuthContext } from './context/AuthContext';
import RoutesConfig from './routes/RoutesConfig';
import MainLayout from './components/MainLayout';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useOrderSocket } from './hooks/useOrderSocket';
import NotificationPermission from './components/NotificationPermission';
import { initializeAudio } from './utils/playNotificationSound';

const App: React.FC = () => {
  const { user, loading } = useContext(AuthContext);

  // Initialize audio when app loads
  useEffect(() => {
    initializeAudio();
  }, []);

  // Scrolling over a focused number input silently changes its value (a browser
  // default) — a real hazard on price fields. Blur the field on wheel so the
  // page scrolls instead of nudging the number.
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      const el = document.activeElement as HTMLInputElement | null;
      if (el && el.tagName === "INPUT" && el.type === "number") {
        el.blur();
      }
    };
    document.addEventListener("wheel", onWheel, { passive: true });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);

  // Always call hook - let it handle conditional logic internally
  useOrderSocket();

  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      <CssBaseline />
        <ToastContainer />
        <NotificationPermission />
        {user ? <MainLayout /> : <RoutesConfig />}
    </Router>
  );
};

export default App;